/**
 * Theme manager — light/dark mode con preferencia persistente y respeto al SO.
 *
 * Reglas:
 *   - `<html data-theme="dark">` activa el bloque dark en CSS (styles.src.css :48).
 *   - localStorage key: `panache:theme` → `'light' | 'dark'` (preferencia manual).
 *   - Sin preferencia manual: respeta `prefers-color-scheme` del SO.
 *
 * Este módulo NO se auto-inicializa — esa responsabilidad es de `theme-boot.js`,
 * que se carga sync en `<head>` para evitar FOUC. Este módulo provee la API
 * runtime (toggle desde UI, listener de cambios del SO, etc.).
 *
 * Si la paleta de tokens cambia en `styles.src.css`, actualizar también `META_COLORS`
 * acá — el browser no resuelve `var(--bg-base)` dentro de `<meta name="theme-color">`.
 */

const STORAGE_KEY = 'panache:theme';
const VALID_THEMES = new Set(['light', 'dark']);

// Duplicación necesaria del valor de `--bg-base` para `<meta name="theme-color">`,
// ya que ese atributo solo acepta colores literales, no CSS vars.
const META_COLORS = {
    light: '#ffffff',
    dark: '#0c0b0a',
};

/**
 * Devuelve la preferencia manual guardada por el usuario, o null si no hay.
 * @returns {'light' | 'dark' | null}
 */
export function getStoredPreference() {
    let stored = null;
    try {
        stored = localStorage.getItem(STORAGE_KEY);
    } catch (_) {
        // localStorage puede estar deshabilitado (private browsing en algunos browsers).
        return null;
    }
    return VALID_THEMES.has(stored) ? stored : null;
}

/**
 * Devuelve la preferencia del sistema operativo via prefers-color-scheme.
 * @returns {'light' | 'dark'}
 */
export function getSystemPreference() {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Devuelve el theme efectivo: preferencia manual si existe, sino CLARO por
 * defecto (decisión de producto — ya NO seguimos el SO). `getSystemPreference`
 * se mantiene exportada por si se necesita, pero no participa del default.
 * @returns {'light' | 'dark'}
 */
export function getTheme() {
    return getStoredPreference() ?? 'light';
}

/**
 * Aplica el theme al DOM (data-theme + meta theme-color) SIN persistir.
 * Útil para el listener del SO: si el usuario no tiene preferencia manual,
 * queremos seguir al SO sin crear una preferencia accidentalmente.
 * @param {'light' | 'dark'} themeValue
 */
export function applyTheme(themeValue) {
    if (!VALID_THEMES.has(themeValue)) {
        throw new Error(`Invalid theme: ${themeValue}. Must be 'light' or 'dark'.`);
    }
    document.documentElement.setAttribute('data-theme', themeValue);
    updateMetaThemeColor(themeValue);
}

/**
 * Aplica el theme al DOM y lo persiste como preferencia manual del usuario.
 * @param {'light' | 'dark'} themeValue
 */
export function setTheme(themeValue) {
    applyTheme(themeValue); // valida también
    try {
        localStorage.setItem(STORAGE_KEY, themeValue);
    } catch (_) {
        // localStorage deshabilitado — el cambio aplica solo para esta sesión.
    }
}

/**
 * Alterna entre dark y light, persiste, y devuelve el nuevo valor.
 * Si no hay preferencia manual, el "current" se calcula desde el SO.
 * @returns {'light' | 'dark'}
 */
export function toggleTheme() {
    const next = getTheme() === 'dark' ? 'light' : 'dark';
    setTheme(next);
    return next;
}

/**
 * Borra la preferencia manual — el theme vuelve a seguir el SO.
 */
export function clearStoredPreference() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
        // no-op
    }
}

/**
 * Actualiza el <meta name="theme-color"> con el hex correspondiente al theme.
 * Si el meta no existe en el documento, es no-op.
 * @param {'light' | 'dark'} themeValue
 */
export function updateMetaThemeColor(themeValue) {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', META_COLORS[themeValue]);
}

/**
 * Escucha cambios en el SO (prefers-color-scheme) y llama callback SOLO si el
 * usuario NO tiene preferencia manual guardada. Esto preserva la intención del
 * usuario: si eligió light explícitamente, no queremos que el SO lo override.
 *
 * @param {(theme: 'light' | 'dark') => void} callback
 * @returns {() => void} función para cancelar la suscripción
 */
export function initSystemListener(callback) {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
        if (getStoredPreference() === null) {
            callback(e.matches ? 'dark' : 'light');
        }
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
}
